import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDown, ChevronUp, Info, Loader2, TrendingUp } from 'lucide-react';
import { useSelector } from 'react-redux';

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Badge,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Switch,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
  Label,
} from '@design-system';
import { Button } from '@client/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CurrencyInput, PercentInput } from '@client/components/NumericInput';
import { cn } from '@client/lib';
import { formatCurrency, formatLtcCostDisplay, carePhaseDefList } from '@shared';
import { formatCompactCurrency } from '@client/util/formatCompactCurrency';
import { useLtcCostDisplayPrecision } from '@client/hooks/useLtcCostDisplayPrecision';
import { selectClient } from '@client/model/selectors';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useSelfFundingProjectedValue } from '@client/hooks/useSelfFundingProjectedValue';

import { type AssetTypeId } from '@/features/guided-financial-planning/config';
import { useIsSessionComplete } from '@/features/guided-financial-planning/context';
import { useSelfFundingForm } from '@/features/guided-financial-planning/hooks';
import { guidedPlanningApi } from '@/features/guided-financial-planning/api';
import { PartnerAllocationSection, type AllocationOption } from './partner-allocation-section';

type SaveState = 'idle' | 'saving' | 'saved';

interface AssetFieldLabels {
  currentValue: { label: string; placeholder: string };
  yearlyContribution: { label: string; placeholder: string };
  rateOfReturn: { label: string; placeholder: string };
  taxRate: { label: string; placeholder: string };
}

const DEFAULT_FIELD_LABELS: AssetFieldLabels = {
  currentValue: { label: 'CURRENT BALANCE', placeholder: 'Enter current balance' },
  yearlyContribution: { label: 'ASSUMED YEARLY CONTRIBUTION', placeholder: 'Enter yearly amount' },
  rateOfReturn: { label: 'ASSUMED ANNUAL GROWTH', placeholder: 'Enter annual % assumption' },
  taxRate: { label: 'EST. TAX RATE ON WITHDRAWAL', placeholder: 'Enter estimated tax %' },
};

const ASSET_FIELD_LABELS: Partial<Record<AssetTypeId, Partial<AssetFieldLabels>>> = {
  'roth-ira': {
    taxRate: { label: 'EST. TAX RATE ON WITHDRAWAL', placeholder: '0% only if qualified' },
  },
  'roth-401k': {
    taxRate: { label: 'EST. TAX RATE ON WITHDRAWAL', placeholder: '0% only if qualified' },
  },
  'traditional-ira': {
    taxRate: { label: 'EST. INCOME TAX RATE ON WITHDRAWAL', placeholder: 'Enter estimated tax %' },
  },
  'traditional-401k': {
    taxRate: { label: 'EST. INCOME TAX RATE ON WITHDRAWAL', placeholder: 'Enter estimated tax %' },
  },
  'hsa': {
    taxRate: { label: 'EST. TAX RATE ON WITHDRAWAL', placeholder: '0% if qualified expense' },
  },
  'brokerage': {
    currentValue: { label: 'CURRENT ACCOUNT VALUE', placeholder: 'Enter current balance' },
    taxRate: { label: 'EST. TAX RATE ON GAIN', placeholder: 'Enter rate on gains only' },
  },
  'home-equity': {
    currentValue: { label: 'CURRENT AVAILABLE EQUITY', placeholder: 'Enter current home equity' },
    yearlyContribution: {
      label: 'YEARLY EQUITY ADDED',
      placeholder: 'Principal paydown or added equity',
    },
    rateOfReturn: { label: 'ASSUMED HOME VALUE GROWTH', placeholder: 'Enter annual home growth %' },
    taxRate: {
      label: 'EST. COST / TAX TO ACCESS EQUITY',
      placeholder: '0% if borrowing; otherwise estimate',
    },
  },
  'retirement-income': {
    currentValue: { label: 'PROJECTED ANNUAL INCOME AT LTC START AGE', placeholder: 'e.g. $50,000' },
    taxRate: { label: 'EST. TAX RATE ON PAYMENTS', placeholder: 'Enter estimated tax %' },
  },
  'cryptocurrency': {
    currentValue: { label: 'CURRENT HOLDINGS VALUE', placeholder: 'Enter current balance' },
    taxRate: { label: 'EST. TAX RATE ON GAIN', placeholder: 'Enter rate on gains only' },
  },
};

function getFieldLabels(assetTypeId: AssetTypeId): AssetFieldLabels {
  const overrides = ASSET_FIELD_LABELS[assetTypeId];
  if (!overrides) return DEFAULT_FIELD_LABELS;
  return {
    currentValue: overrides.currentValue ?? DEFAULT_FIELD_LABELS.currentValue,
    yearlyContribution: overrides.yearlyContribution ?? DEFAULT_FIELD_LABELS.yearlyContribution,
    rateOfReturn: overrides.rateOfReturn ?? DEFAULT_FIELD_LABELS.rateOfReturn,
    taxRate: overrides.taxRate ?? DEFAULT_FIELD_LABELS.taxRate,
  };
}

function LoadingOverlay({ state }: { state: SaveState }) {
  if (state === 'idle') return null;

  return (
    <div
      className={cn(
        'absolute inset-0 z-10 flex scale-105 flex-col items-center justify-center rounded-lg bg-white/90 backdrop-blur-sm transition-opacity duration-500',
        state === 'saved' ? 'opacity-0' : 'opacity-100',
      )}
    >
      {state === 'saving' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <p className="text-sm font-medium text-gray-700">Saving...</p>
        </div>
      )}
      {state === 'saved' && (
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
            <CheckIcon className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-sm font-medium text-green-600">Saved</p>
        </div>
      )}
    </div>
  );
}

const YEAR_PRESETS = [5, 10, 15, 20] as const;
const CURRENT_YEAR = new Date().getFullYear();

function isPresetYear(value: number | null | undefined): boolean {
  return value != null && YEAR_PRESETS.some(y => CURRENT_YEAR + y === value);
}

function YearPresetSelect({
  label,
  defaultLabel,
  value,
  onChange,
  disabled,
}: {
  label: string;
  defaultLabel: string;
  value: number | null | undefined;
  onChange: (year: number | null) => void;
  disabled?: boolean;
}) {
  const [isCustom, setIsCustom] = useState(() => value != null && !isPresetYear(value));

  const selectValue = value == null ? 'default' : isCustom ? 'custom' : String(value);

  const handleSelectChange = useCallback(
    (val: string) => {
      if (val === 'default') {
        setIsCustom(false);
        onChange(null);
      } else if (val === 'custom') {
        setIsCustom(true);
      } else {
        setIsCustom(false);
        onChange(Number(val));
      }
    },
    [onChange],
  );

  if (isCustom) {
    return (
      <div className="space-y-1.5">
        <Label className="inline-block text-[10px] font-semibold uppercase">{label}</Label>
        <div className="flex gap-2">
          <Input
            type="number"
            placeholder="e.g. 2035"
            value={value ?? ''}
            onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
            onInput={e => {
              const input = e.target as HTMLInputElement;
              if (input.value.length > 4) input.value = input.value.slice(0, 4);
            }}
            disabled={disabled}
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setIsCustom(false);
              onChange(null);
            }}
            className="shrink-0"
          >
            Reset
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label className="inline-block text-[10px] font-semibold uppercase">{label}</Label>
      <Select
        value={selectValue}
        onValueChange={handleSelectChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={defaultLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="default">{defaultLabel}</SelectItem>
          {YEAR_PRESETS.map(years => (
            <SelectItem
              key={years}
              value={String(CURRENT_YEAR + years)}
            >
              In {years} years ({CURRENT_YEAR + years})
            </SelectItem>
          ))}
          <SelectItem value="custom">Custom year</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

interface CarePhaseYears {
  earlyCare: number | null;
  moderateCare: number | null;
  fullCare: number | null;
}

function CarePhaseYearSelect({
  label,
  defaultLabel,
  value,
  onChange,
  disabled,
  carePhaseYears,
}: {
  label: string;
  defaultLabel: string;
  value: number | null | undefined;
  onChange: (year: number | null) => void;
  disabled?: boolean;
  carePhaseYears: CarePhaseYears;
}) {
  const presetYears = carePhaseDefList
    .map(def => ({
      key: def.key,
      label: def.label,
      year: carePhaseYears[def.key as keyof CarePhaseYears],
    }))
    .filter(p => p.year != null)
    .map(p => ({ ...p, year: Math.round(p.year!) }));

  const hasPresets = presetYears.length > 0;

  const isPreset = value != null && presetYears.some(p => p.year === value);
  const [isCustom, setIsCustom] = useState(() => value != null && !isPreset);

  const selectValue = value == null ? 'default' : isCustom ? 'custom' : String(value);

  const handleSelectChange = useCallback(
    (val: string) => {
      if (val === 'default') {
        setIsCustom(false);
        onChange(null);
      } else if (val === 'custom') {
        setIsCustom(true);
      } else {
        setIsCustom(false);
        onChange(Number(val));
      }
    },
    [onChange],
  );

  // No care phases available — show a plain year input with a reset to default
  if (!hasPresets) {
    return (
      <div className="space-y-1.5">
        <Label className="inline-block text-[10px] font-semibold uppercase">{label}</Label>
        {value != null ? (
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder="e.g. 2035"
              value={value ?? ''}
              onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
              onInput={e => {
                const input = e.target as HTMLInputElement;
                if (input.value.length > 4) input.value = input.value.slice(0, 4);
              }}
              disabled={disabled}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => onChange(null)}
              className="shrink-0"
            >
              Reset
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder={defaultLabel}
              value=""
              onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
              onInput={e => {
                const input = e.target as HTMLInputElement;
                if (input.value.length > 4) input.value = input.value.slice(0, 4);
              }}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    );
  }

  if (isCustom) {
    return (
      <div className="space-y-1.5">
        <Label className="inline-block text-[10px] font-semibold uppercase">{label}</Label>
        <div className="flex gap-2">
          <Input
            type="number"
            placeholder="e.g. 2035"
            value={value ?? ''}
            onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
            onInput={e => {
              const input = e.target as HTMLInputElement;
              if (input.value.length > 4) input.value = input.value.slice(0, 4);
            }}
            disabled={disabled}
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setIsCustom(false);
              onChange(null);
            }}
            className="shrink-0"
          >
            Reset
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label className="inline-block text-[10px] font-semibold uppercase">{label}</Label>
      <Select
        value={selectValue}
        onValueChange={handleSelectChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={defaultLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="default">{defaultLabel}</SelectItem>
          {presetYears.map(p => (
            <SelectItem
              key={p.key}
              value={String(p.year)}
            >
              Start of {p.label} ({p.year})
            </SelectItem>
          ))}
          <SelectItem value="custom">Custom year</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

export interface AllocationCallbacks {
  hasPartner: boolean;
  clientName: string;
  partnerName: string;
  initialState: {
    enabled: boolean;
    option: AllocationOption;
    customClientPercent: number;
    originalValue?: number;
  };
  onApply: (
    state: {
      enabled: boolean;
      option: AllocationOption;
      customClientPercent: number;
      formValue?: number;
    },
    overrides?: {
      fundingSourceId: string;
      sourceData: SelfFundingSource;
    },
  ) => Promise<void>;
}

interface AccountCardProps {
  assetTypeId: AssetTypeId;
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  fundingSourceId: string | null;
  existingSource?: SelfFundingSource;
  onSaveComplete: () => void;
  disabled?: boolean;
  allocationAmount?: number;
  lockedByPartnerName?: string;
  allocation?: AllocationCallbacks;
  hideHeader?: boolean;
}

function isSourceComplete(source?: SelfFundingSource): boolean {
  if (!source) return false;
  if (source.accountType === 'retirementIncome') {
    return source.existingAssetsValue !== null && source.gainsTaxRate !== null;
  }
  return (
    source.existingAssetsValue !== null &&
    source.annualRateOfReturn !== null &&
    source.gainsTaxRate !== null
  );
}

export function AccountCard({
  assetTypeId,
  label,
  isExpanded,
  onToggle,
  fundingSourceId,
  existingSource,
  onSaveComplete,
  disabled,
  allocationAmount,
  lockedByPartnerName,
  allocation,
  hideHeader,
}: AccountCardProps) {
  const isLocked = !!lockedByPartnerName;
  const isComplete = isSourceComplete(existingSource);
  const { form, onSubmit, buildSourceData, isSaving, clientId, isRetirementIncome } =
    useSelfFundingForm(assetTypeId, fundingSourceId, existingSource);
  const client = useSelector(selectClient);
  const watchedValues = useWatch({
    control: form.control,
    name: ['existingAssetsValue', 'yearlyContribution', 'annualRateOfReturn', 'gainsTaxRate'],
  });
  const [existingAssetsValue, yearlyContribution, annualRateOfReturn, gainsTaxRate] =
    watchedValues ?? [];

  const moreOptionsDefaultOpen =
    existingSource?.contributionStopYear != null || !!existingSource?.hasDrawdown;
  const contributionStopYear = form.watch('contributionStopYear');
  const hasDrawdown = form.watch('hasDrawdown');
  const drawdownStartAtYear = form.watch('drawdownStartAtYear');
  const drawdownEndAtYear = form.watch('drawdownEndAtYear');
  const drawdownAmount = form.watch('drawdownAmount');
  const drawdownAmountInterval = form.watch('drawdownAmountInterval');

  const selfFundingStateForPreview = useMemo((): Partial<SelfFundingSource> | null => {
    if (isRetirementIncome) {
      if (existingAssetsValue == null || gainsTaxRate == null) return null;
      return {
        existingAssetsValue: typeof existingAssetsValue === 'number' ? existingAssetsValue : null,
        monthlyContributionAmount: null,
        annualRateOfReturn: 0,
        gainsTaxRate: typeof gainsTaxRate === 'number' ? gainsTaxRate : null,
        monthlyIncome: null,
        accountType: 'retirementIncome',
      };
    }

    if (existingAssetsValue == null || annualRateOfReturn == null || gainsTaxRate == null) {
      return null;
    }
    return {
      existingAssetsValue: typeof existingAssetsValue === 'number' ? existingAssetsValue : null,
      monthlyContributionAmount:
        yearlyContribution != null && typeof yearlyContribution === 'number'
          ? yearlyContribution / 12
          : null,
      annualRateOfReturn: typeof annualRateOfReturn === 'number' ? annualRateOfReturn : null,
      gainsTaxRate: typeof gainsTaxRate === 'number' ? gainsTaxRate : null,
      monthlyIncome: existingSource?.monthlyIncome ?? null,
      contributionStopYear: contributionStopYear ?? null,
      hasDrawdown: hasDrawdown ?? false,
      drawdownStartAtYear: hasDrawdown ? (drawdownStartAtYear ?? null) : null,
      drawdownEndAtYear: hasDrawdown ? (drawdownEndAtYear ?? null) : null,
      drawdownAmount: hasDrawdown ? (drawdownAmount ?? null) : null,
      drawdownAmountInterval: hasDrawdown ? (drawdownAmountInterval ?? 'yearly') : null,
    };
  }, [
    isRetirementIncome,
    existingAssetsValue,
    yearlyContribution,
    annualRateOfReturn,
    gainsTaxRate,
    existingSource?.monthlyIncome,
    contributionStopYear,
    hasDrawdown,
    drawdownStartAtYear,
    drawdownEndAtYear,
    drawdownAmount,
    drawdownAmountInterval,
  ]);

  const { ltcAtYear, yearsTillLtc, ltcAtAge } = client?.appliedInferenceSet ?? {};
  const clientBirthDate = client?.intakeSurvey?.clientBirthDate;
  const carePhaseYears: CarePhaseYears = useMemo(() => {
    const startYears = client?.clientPhasePredictedStartYears;
    if (!startYears) return { earlyCare: null, moderateCare: null, fullCare: null };
    return {
      earlyCare: startYears[carePhaseDefList[0].value] ?? null,
      moderateCare: startYears[carePhaseDefList[1].value] ?? null,
      fullCare: startYears[carePhaseDefList[2].value] ?? null,
    };
  }, [client?.clientPhasePredictedStartYears]);
  const allPhaseCareMonthsNeeded = client?.allPhaseCosts?.allPhaseCareMonthsNeeded;

  const { projectedValue, ageAtLtc } = useSelfFundingProjectedValue(selfFundingStateForPreview, {
    ltcAtYear: ltcAtYear ?? new Date().getFullYear(),
    yearsTillLtc: Math.max(yearsTillLtc ?? 0, 0),
    allPhaseCareMonthsNeeded,
    ltcAtAge: ltcAtAge ?? undefined,
    clientBirthDate: clientBirthDate ?? undefined,
  });

  const { flags } = useFeatureFlags();
  const roundingStep = useLtcCostDisplayPrecision();
  const showPreview = flags.selfFundingGrowthPreview === true && projectedValue != null;

  const currentValueFormatted = formatCompactCurrency(
    typeof existingAssetsValue === 'number' ? existingAssetsValue : null,
  );
  const previewTooltipCopy = isRetirementIncome
    ? 'Projected after-tax income available over the entire care period, starting at predicted long-term care age.'
    : 'Current account value and projected value at predicted long-term care age, based on your growth and tax assumptions.';

  const previewBadgeContent = showPreview ? (
    <>
      <span className="text-gray-700">
        {
          formatLtcCostDisplay(
            typeof existingAssetsValue === 'number' ? existingAssetsValue : undefined,
            { roundingStep },
          ).formattedDisplay
        }
      </span>
      <span className="mx-1"></span>
      <span className="inline-flex items-center font-semibold">
        <TrendingUp className="mr-1 size-3.5 shrink-0" />
        {formatLtcCostDisplay(projectedValue ?? undefined, { roundingStep }).formattedDisplay}
      </span>
      <span className="ml-1 text-gray-700">(age {ageAtLtc ?? ltcAtYear})</span>
    </>
  ) : null;

  const fieldLabels = getFieldLabels(assetTypeId);
  if (isRetirementIncome && ltcAtAge != null) {
    fieldLabels.currentValue = {
      ...fieldLabels.currentValue,
      label: `PROJECTED ANNUAL INCOME AT LTC START AGE (${ltcAtAge})`,
    };
  }
  const isSessionComplete = useIsSessionComplete();
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const prevIsSavingRef = useRef(false);
  const saveCompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Allocation section state
  const [allocationEnabled, setAllocationEnabled] = useState(
    allocation?.initialState.enabled ?? false,
  );
  const [allocationOption, setAllocationOption] = useState<AllocationOption>(
    allocation?.initialState.option ?? 'mirror',
  );
  const [customClientPercent, setCustomClientPercent] = useState(
    allocation?.initialState.customClientPercent ?? 62,
  );

  // Sync allocation state when initial state changes (e.g. session reload)
  const prevAllocationKey = useRef<string | null>(null);
  useEffect(() => {
    if (!allocation) return;
    const key = `${fundingSourceId}-${allocation.initialState.enabled}-${allocation.initialState.option}-${allocation.initialState.customClientPercent}`;
    if (prevAllocationKey.current === key) return;
    prevAllocationKey.current = key;
    setAllocationEnabled(allocation.initialState.enabled);
    setAllocationOption(allocation.initialState.option);
    setCustomClientPercent(allocation.initialState.customClientPercent);

    // Show pre-split total in the form so user always edits the full amount
    if (allocation.initialState.enabled && allocation.initialState.originalValue != null) {
      form.setValue('existingAssetsValue', allocation.initialState.originalValue);
    }
  }, [allocation, fundingSourceId, form]);

  // After Redux refreshes the source with the split value, restore the pre-split original
  useEffect(() => {
    if (!allocation?.initialState.enabled) return;
    const originalValue = allocation.initialState.originalValue;
    if (originalValue == null) return;
    if (
      existingSource?.existingAssetsValue != null &&
      existingSource.existingAssetsValue !== originalValue
    ) {
      form.setValue('existingAssetsValue', originalValue);
    }
  }, [
    existingSource?.existingAssetsValue,
    allocation?.initialState.enabled,
    allocation?.initialState.originalValue,
    form,
  ]);

  // Track save state transitions
  useEffect(() => {
    const prevIsSaving = prevIsSavingRef.current;

    if (!prevIsSaving && isSaving) {
      setSaveState('saving');
    } else if (prevIsSaving && !isSaving && saveState === 'saving') {
      setSaveState('saved');

      saveCompleteTimeoutRef.current = setTimeout(() => {
        setSaveState('idle');
        onSaveComplete();
      }, 800);
    }

    prevIsSavingRef.current = isSaving;
  }, [isSaving, saveState, onSaveComplete]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveCompleteTimeoutRef.current) {
        clearTimeout(saveCompleteTimeoutRef.current);
      }
    };
  }, []);

  const [isApplyingAllocation, setIsApplyingAllocation] = useState(false);

  const handleSubmit = useCallback(async () => {
    const isFirstSaveWithAllocation = allocation && !fundingSourceId && allocationEnabled;

    if (isFirstSaveWithAllocation) {
      // Validate form first
      const isValid = await form.trigger();
      if (!isValid) return;

      setSaveState('saving');
      setIsApplyingAllocation(true);
      try {
        const formValues = form.getValues();
        const sourceData = buildSourceData(formValues);

        // Save via API directly so we get the fundingSourceId back
        const response = await guidedPlanningApi.upsertSelfFunding({
          clientId,
          fundingSourceId: null,
          fundingSourceData: sourceData,
        });

        // Apply allocation with the newly created fundingSourceId
        await allocation.onApply(
          {
            enabled: allocationEnabled,
            option: allocationOption,
            customClientPercent,
            formValue: formValues.existingAssetsValue,
          },
          {
            fundingSourceId: response.fundingSourceId,
            sourceData,
          },
        );

        setSaveState('saved');
        setTimeout(() => {
          setSaveState('idle');
          onSaveComplete();
        }, 800);
      } catch {
        setSaveState('idle');
      } finally {
        setIsApplyingAllocation(false);
      }
    } else {
      // Standard flow: save form via Redux, then apply allocation
      const isValid = await form.trigger();
      if (!isValid) return;

      await form.handleSubmit(onSubmit)();

      if (allocation && fundingSourceId && existingSource) {
        setIsApplyingAllocation(true);
        try {
          const formValues = form.getValues();
          const sourceData = buildSourceData(formValues);
          await allocation.onApply(
            {
              enabled: allocationEnabled,
              option: allocationOption,
              customClientPercent,
              formValue:
                typeof formValues.existingAssetsValue === 'number'
                  ? formValues.existingAssetsValue
                  : undefined,
            },
            {
              fundingSourceId,
              sourceData,
            },
          );
        } finally {
          setIsApplyingAllocation(false);
        }
      }
    }
  }, [
    form,
    onSubmit,
    buildSourceData,
    clientId,
    allocation,
    fundingSourceId,
    existingSource,
    allocationEnabled,
    allocationOption,
    customClientPercent,
    onSaveComplete,
  ]);

  const accountValue =
    typeof existingAssetsValue === 'number'
      ? existingAssetsValue
      : (existingSource?.existingAssetsValue ?? 0);

  const formContent = (
    <>
      <div className="relative">
        <LoadingOverlay state={saveState} />

        {showPreview && !hideHeader && (
          <Badge
            variant="secondary"
            className="mb-4 w-fit font-normal sm:hidden"
          >
            {ageAtLtc != null
              ? `Projected value by age ${ageAtLtc} : `
              : `Projected value by ${ltcAtYear} : `}
            <span className="font-semibold">
              {formatLtcCostDisplay(projectedValue ?? undefined, { roundingStep }).formattedDisplay}
            </span>
          </Badge>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            name="existingAssetsValue"
            control={form.control}
            render={({ field, fieldState }) => (
              <CurrencyInput
                label={fieldLabels.currentValue.label}
                placeholder={fieldLabels.currentValue.placeholder}
                value={field.value ?? null}
                onChange={field.onChange}
                onPreview={field.onChange}
                errorMessage={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />

          {!isRetirementIncome && (
            <Controller
              name="yearlyContribution"
              control={form.control}
              render={({ field, fieldState }) => (
                <CurrencyInput
                  label={fieldLabels.yearlyContribution.label}
                  placeholder={fieldLabels.yearlyContribution.placeholder}
                  value={field.value ?? null}
                  onChange={field.onChange}
                  onPreview={field.onChange}
                  errorMessage={fieldState.error?.message}
                  disabled={disabled}
                />
              )}
            />
          )}

          {!isRetirementIncome && (
            <Controller
              name="annualRateOfReturn"
              control={form.control}
              render={({ field, fieldState }) => (
                <PercentInput
                  label={fieldLabels.rateOfReturn.label}
                  placeholder={fieldLabels.rateOfReturn.placeholder}
                  value={field.value ?? null}
                  onChange={field.onChange}
                  onPreview={field.onChange}
                  errorMessage={fieldState.error?.message}
                  disabled={disabled}
                />
              )}
            />
          )}

          <Controller
            name="gainsTaxRate"
            control={form.control}
            render={({ field, fieldState }) => (
              <PercentInput
                label={fieldLabels.taxRate.label}
                placeholder={fieldLabels.taxRate.placeholder}
                value={field.value ?? null}
                onChange={field.onChange}
                onPreview={field.onChange}
                errorMessage={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />
        </div>

        {/* More options accordion */}
        {flags.selfFundingAdvancedOptions && !isRetirementIncome && (
        <Accordion
          type="single"
          collapsible
          defaultValue={moreOptionsDefaultOpen ? 'more-options' : undefined}
          className="mt-4"
        >
          <AccordionItem
            value="more-options"
            className="data-[state=open]:bg-transparent"
          >
            <AccordionTrigger className="py-3 pl-4 pr-3 text-sm">More Options</AccordionTrigger>
            <AccordionContent className="border-t px-4 pb-4 pt-2">
              <div className="space-y-4">
                {/* ── Contribution End Date ── */}
                <Controller
                  name="contributionStopYear"
                  control={form.control}
                  render={({ field }) => (
                    <YearPresetSelect
                      label="Stop Assumed Yearly Contribution At"
                      defaultLabel="At LTC start (default)"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={disabled}
                    />
                  )}
                />

                {/* ── Withdrawals ── */}
                <div className="space-y-2 border-t pt-4">
                  <label
                    htmlFor="has-drawdown"
                    className="flex cursor-pointer items-center justify-between"
                  >
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase">
                      Withdrawals
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="text-muted-foreground size-3.5 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-xs text-xs font-normal normal-case"
                        >
                          Expected withdrawals from this account before care begins. This reduces
                          the projected value available for long-term care funding.
                        </TooltipContent>
                      </Tooltip>
                    </span>
                    <Controller
                      name="hasDrawdown"
                      control={form.control}
                      render={({ field }) => (
                        <Switch
                          id="has-drawdown"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={disabled}
                        />
                      )}
                    />
                  </label>

                  {hasDrawdown && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Controller
                          name="drawdownStartAtYear"
                          control={form.control}
                          render={({ field }) => (
                            <CarePhaseYearSelect
                              label="Withdrawals begin"
                              defaultLabel="Starting now"
                              value={field.value}
                              onChange={field.onChange}
                              disabled={disabled}
                              carePhaseYears={carePhaseYears}
                            />
                          )}
                        />

                        <Controller
                          name="drawdownEndAtYear"
                          control={form.control}
                          render={({ field }) => (
                            <CarePhaseYearSelect
                              label="Withdrawals end"
                              defaultLabel="At LTC start"
                              value={field.value}
                              onChange={field.onChange}
                              disabled={disabled}
                              carePhaseYears={carePhaseYears}
                            />
                          )}
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="inline-block text-[10px] font-semibold uppercase">
                          Withdrawal amount
                        </Label>
                        <div className="flex items-center gap-2">
                          <Controller
                            name="drawdownAmount"
                            control={form.control}
                            render={({ field }) => (
                              <InputGroup className="flex-1">
                                <InputGroupAddon align="inline-start">$</InputGroupAddon>
                                <InputGroupInput
                                  inputMode="numeric"
                                  placeholder="Enter amount"
                                  value={
                                    field.value != null ? field.value.toLocaleString('en-US') : ''
                                  }
                                  onChange={e => {
                                    const raw = e.target.value.replace(/,/g, '');
                                    field.onChange(raw ? Number(raw) : null);
                                  }}
                                  disabled={disabled}
                                />
                              </InputGroup>
                            )}
                          />
                          <Controller
                            name="drawdownAmountInterval"
                            control={form.control}
                            render={({ field }) => (
                              <ToggleGroup
                                type="single"
                                value={field.value}
                                onValueChange={val => {
                                  if (val) field.onChange(val);
                                }}
                                variant="outline"
                                className="shrink-0"
                              >
                                <ToggleGroupItem value="monthly">Monthly</ToggleGroupItem>
                                <ToggleGroupItem value="yearly">Yearly</ToggleGroupItem>
                              </ToggleGroup>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        )}
      </div>

      {/* Partner allocation section */}
      {allocation?.hasPartner && (
        <PartnerAllocationSection
          enabled={allocationEnabled}
          onEnabledChange={setAllocationEnabled}
          value={accountValue}
          clientName={allocation.clientName}
          partnerName={allocation.partnerName}
          selectedOption={allocationOption}
          onOptionChange={setAllocationOption}
          customClientPercent={customClientPercent}
          onCustomClientPercentChange={setCustomClientPercent}
          disabled={disabled}
        />
      )}

      {!disabled && (
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSaving || isApplyingAllocation}
          className={cn('mt-4 w-full rounded-full', {
            'rounded-md': hideHeader,
          })}
        >
          {isApplyingAllocation ? 'Saving...' : 'Save'}
        </Button>
      )}
    </>
  );

  if (hideHeader) {
    return <div>{formContent}</div>;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-900">{label}</span>
        </div>

        <div className="flex min-w-0 max-w-[65%] items-center justify-end gap-3">
          {showPreview && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex min-w-0 max-w-full shrink cursor-default">
                  <Badge
                    variant="secondary"
                    className="hidden min-w-0 max-w-full shrink truncate font-normal sm:inline-flex"
                  >
                    {previewBadgeContent}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs"
              >
                <p>{previewTooltipCopy}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {!showPreview && isComplete && existingSource?.existingAssetsValue != null && (
            <Badge
              variant="secondary"
              className="hidden font-medium sm:inline-flex"
            >
              {formatCurrency(existingSource.existingAssetsValue)}
              {isSessionComplete && allocationAmount && allocationAmount > 0 && (
                <span className="ml-1 text-gray-500">(−{formatCurrency(allocationAmount)})</span>
              )}
            </Badge>
          )}
          {flags.selfFundingGrowthPreview === true &&
            (showPreview || (isComplete && existingSource?.existingAssetsValue != null)) && (
              <Badge
                variant="secondary"
                className="font-medium sm:hidden"
              >
                {existingSource?.existingAssetsValue != null
                  ? formatCurrency(existingSource.existingAssetsValue)
                  : currentValueFormatted}
                {isSessionComplete &&
                  allocationAmount &&
                  allocationAmount > 0 &&
                  existingSource?.existingAssetsValue != null && (
                    <span className="ml-1 text-gray-500">
                      (−{formatCurrency(allocationAmount)})
                    </span>
                  )}
              </Badge>
            )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </button>

      {/* Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden border-t border-gray-100"
          >
            {isLocked ? (
              <div className="grid grid-cols-2 gap-3 px-5 pb-5 pt-4">
                <div className="rounded-lg bg-gray-50 px-3 py-2.5">
                  <p className="text-foreground text-xs font-medium uppercase tracking-wide">
                    {fieldLabels.currentValue.label}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-950">
                    {existingSource?.existingAssetsValue != null
                      ? formatCurrency(existingSource.existingAssetsValue)
                      : '—'}
                  </p>
                </div>
                {!isRetirementIncome && (
                  <div className="rounded-lg bg-gray-50 px-3 py-2.5">
                    <p className="text-foreground text-xs font-medium uppercase tracking-wide">
                      {fieldLabels.yearlyContribution.label}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-gray-950">
                      {existingSource?.monthlyContributionAmount != null
                        ? formatCurrency(existingSource.monthlyContributionAmount * 12)
                        : '—'}
                    </p>
                  </div>
                )}
                {!isRetirementIncome && (
                  <div className="rounded-lg bg-gray-50 px-3 py-2.5">
                    <p className="text-foreground text-xs font-medium uppercase tracking-wide">
                      {fieldLabels.rateOfReturn.label}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-gray-950">
                      {existingSource?.annualRateOfReturn != null
                        ? `${Math.round(existingSource.annualRateOfReturn * 100)}%`
                        : '—'}
                    </p>
                  </div>
                )}
                <div className="rounded-lg bg-gray-50 px-3 py-2.5">
                  <p className="text-foreground text-xs font-medium uppercase tracking-wide">
                    {fieldLabels.taxRate.label}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-950">
                    {existingSource?.gainsTaxRate != null
                      ? `${Math.round(existingSource.gainsTaxRate * 100)}%`
                      : '—'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="px-5 pb-5 pt-4">{formContent}</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
